import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowUp, Loader2, Plus } from 'lucide-react';
import { AvaMark } from '../../components/dtp/AvaMark';
import SimpleMarkdown from '../../components/agent/SimpleMarkdown';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { patientAva, PatientAvaDestination, usePatientAvaStatus } from '../../lib/patientAva';
import { openPatientSupportChat } from '../../lib/patientSupportChat';
import { SupportChatButton } from '../../components/dtp/PatientSupport';
import { track } from '../../lib/analytics';

/** Where the patient opened Ava from: links pass it as router state. */
const AVA_PLACEMENTS = ['nav', 'account_menu', 'home', 'help_page'] as const;

/**
 * Ask Ava: the portal's AI assistant for questions about the patient's own
 * orders, refills, results and account. Read-only; she points to support chat,
 * the care team or a portal page for anything she can't do or shouldn't answer.
 */

type Turn = { role: 'user' | 'assistant'; text: string; steps: { destination: PatientAvaDestination; label: string }[]; looking?: boolean; error?: boolean };

const PAGES: Partial<Record<PatientAvaDestination, string>> = {
  care_team: '/messages', orders: '/orders', refills: '/refills', treatments: '/treatments', results: '/results',
  billing: '/billing', account: '/account', membership: '/membership', shop: '/shop',
};


export function DtpAva() {
  const { physicianId } = useDtpParams();
  const navigate = useNavigate();
  const { available, starters } = usePatientAvaStatus();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const conversationId = useRef<string | undefined>();
  const abort = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const { state } = useLocation();

  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    const from = (state as { avaPlacement?: string } | null)?.avaPlacement;
    track('ava_opened', { placement: AVA_PLACEMENTS.find(p => p === from) ?? 'direct' });
    // Once per visit to the page, not per navigation state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { end.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' }); }, [turns]);

  const update = (fn: (last: Turn) => Turn) => setTurns(prev => [...prev.slice(0, -1), fn(prev[prev.length - 1])]);

  async function ask(text: string, starter = false) {
    const message = text.trim();
    if (!message || busy) return;
    // Whether it was a suggested starter, never what was asked.
    track('ava_question_sent', { starter });
    setDraft('');
    setBusy(true);
    setTurns(prev => [...prev, { role: 'user', text: message, steps: [] }, { role: 'assistant', text: '', steps: [], looking: true }]);
    abort.current = new AbortController();
    try {
      await patientAva.chat({ conversationId: conversationId.current, message }, event => {
        if (event.type === 'conversation') conversationId.current = event.conversationId;
        else if (event.type === 'text') update(t => ({ ...t, text: t.text + event.text, looking: false }));
        else if (event.type === 'tool') update(t => ({ ...t, looking: true }));
        else if (event.type === 'next_step') update(t => ({ ...t, steps: t.steps.some(s => s.destination === event.destination) ? t.steps : [...t.steps, { destination: event.destination, label: event.label }] }));
        else if (event.type === 'error') update(t => ({ ...t, text: t.text || event.message, error: !t.text, looking: false }));
      }, abort.current.signal);
    } catch (err) {
      update(t => ({ ...t, text: err instanceof Error ? err.message : 'Ava isn’t available right now.', error: true, looking: false }));
    } finally {
      update(t => ({ ...t, looking: false }));
      setBusy(false);
    }
  }

  function go(destination: PatientAvaDestination) {
    track('ava_next_step_clicked', { destination });
    if (destination === 'support_chat') {
      if (!openPatientSupportChat('ava')) navigate(dtpPath(physicianId, '/support'));
      return;
    }
    const page = PAGES[destination];
    if (page) navigate(dtpPath(physicianId, page));
  }

  function newChat() {
    abort.current?.abort();
    conversationId.current = undefined;
    setTurns([]);
    setBusy(false);
  }

  const submit = (event: FormEvent) => { event.preventDefault(); ask(draft); };

  return (
    <div className="mx-auto flex max-w-3xl flex-col">
      <div className="flex items-center gap-3 sm:gap-4">
        <AvaMark size={44} busy={busy} />
        <h1 className="portal-display text-[34px] font-bold leading-none sm:text-[40px]">Ask Ava</h1>
        {turns.length > 0 && <button type="button" onClick={newChat} aria-label="New chat" className="ml-auto inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-white/80 px-3 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white sm:px-4"><Plus aria-hidden="true" className="h-4 w-4" /><span className="hidden sm:inline">New chat</span></button>}
      </div>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Instant answers about your orders, refills, results and account.</p>
      <p className="mt-5 rounded-2xl bg-[#fff4e0] px-4 py-3 text-sm text-[#7a4a00]">Ava is an AI assistant and can make mistakes. She can’t give medical advice; for anything about your health, message your care team. In an emergency, call 911.</p>

      {!available ? (
        <div className="portal-card mt-6 p-6 text-center sm:p-8">
          <p className="portal-display text-lg font-semibold">Ava isn’t available right now</p>
          <p className="mt-2 text-sm text-[var(--portal-muted)]">Our support team can help with orders, payments and your account.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2"><SupportChatButton placement="ava" /><Link to={dtpPath(physicianId, '/support')} className="inline-flex h-11 items-center rounded-full bg-white/80 px-5 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white">Help & support</Link></div>
        </div>
      ) : <>
        <div aria-live="polite" className="mt-6 space-y-5">
          {turns.length === 0 && (
            <div className="portal-card p-6 sm:p-7">
              <p className="portal-display text-lg font-semibold">Hi! What can I help with?</p>
              <p className="mt-1 text-sm text-[var(--portal-muted)]">I can look up your own orders, refills, results and payments, and explain how things work here.</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {starters.map((s: string) => <button key={s} type="button" onClick={() => ask(s, true)} className="rounded-full bg-white/80 px-4 py-2 text-sm font-medium ring-1 ring-[var(--portal-line)] transition hover:bg-white">{s}</button>)}
              </div>
            </div>
          )}
          {turns.map((turn, i) => turn.role === 'user' ? (
            <div key={i} className="flex justify-end"><p className="max-w-[85%] whitespace-pre-wrap rounded-3xl rounded-br-lg bg-[var(--portal-primary)] px-4 py-3 text-[15px] text-[var(--portal-primary-fg)]">{turn.text}</p></div>
          ) : (
            <div key={i} className="flex items-start gap-3">
              <AvaMark size={32} busy={turn.looking} />
              <div className="min-w-0 max-w-[85%]">
                <div className={`rounded-3xl rounded-tl-lg px-4 py-3 ring-1 ${turn.error ? 'bg-[#fde4dc] text-[#8c2a14] ring-transparent' : 'bg-[var(--portal-card-solid)] ring-[var(--portal-line)]'}`}>
                  {turn.text ? <div className="text-[15px] [&_.text-sm]:text-[15px]"><SimpleMarkdown text={turn.text} /></div>
                    : <p role="status" className="flex items-center gap-2 text-sm text-[var(--portal-muted)]"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />Looking that up…</p>}
                </div>
                {turn.steps.length > 0 && <div className="mt-2 flex flex-wrap gap-2">
                  {turn.steps.map(step => <button key={step.destination} type="button" onClick={() => go(step.destination)}
                    className={`inline-flex h-10 items-center rounded-full px-4 text-sm font-semibold transition ${step.destination === 'support_chat' ? 'bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] hover:opacity-90' : 'bg-white/80 ring-1 ring-[var(--portal-line)] hover:bg-white'}`}>{step.label}</button>)}
                </div>}
              </div>
            </div>
          ))}
          <div ref={end} />
        </div>

        <form onSubmit={submit} className="sticky bottom-[calc(env(safe-area-inset-bottom)_+_5rem)] z-10 mt-6 flex items-end gap-2 rounded-[28px] border border-[var(--portal-line)] bg-[var(--portal-card-solid)] p-2 shadow-[0_18px_40px_-24px_rgba(36,26,21,0.45)] lg:bottom-4">
          <label htmlFor="ava-input" className="sr-only">Ask Ava</label>
          <textarea id="ava-input" rows={1} maxLength={2000} value={draft} onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(draft); } }}
            placeholder="Ask about an order or a refill…" className="max-h-40 min-h-[44px] flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] focus:outline-none" />
          <button type="submit" disabled={busy || !draft.trim()} aria-label="Send"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[var(--portal-primary)] text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-40">
            {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <ArrowUp aria-hidden="true" className="h-5 w-5" />}
          </button>
        </form>
      </>}
    </div>
  );
}

export default DtpAva;
